import { Drawer } from '@mui/material';
import styled, { css } from 'styled-components';

interface IsSmProps {
  $isSm: boolean;
}

// Matches the left-nav drawer: a slow, smooth easeInOut width slide.
const widthTransition = css`
  ${({ theme }) =>
    theme.transitions.create('width', {
      easing: theme.transitions.easing.easeInOut,
      duration: theme.transitions.duration.standard,
    })}
`;

/**
 * Holds the main panel and (on lg+) the resize handle + side panel in a row.
 * The right + bottom padding reproduce the content panel's retained margin;
 * the resize handle supplies the equal gap between the two panels.
 */
const ContentRow = styled.div<IsSmProps>`
  flex: 1 1 auto;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: row;
  padding: ${({ theme, $isSm }) => ($isSm ? `0 ${theme.spacing(2)} ${theme.spacing(2)} 0` : '0')};
`;

const MainContentPanel = styled.div<IsSmProps>`
  flex: 1 1 auto;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  background-color: ${({ theme }) => theme.palette.background.canvas};
  /* On mobile the panel is flush with the top of the screen (no app bar), so it
     is fully square; on md+ it floats with all corners rounded. */
  border-radius: ${({ $isSm }) => ($isSm ? '24px' : '0')};
  overflow: hidden;
`;

const SideContentPanel = styled.div<{ $resizing: boolean }>`
  flex: 0 0 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  background-color: ${({ theme }) => theme.palette.background.canvas};
  border-radius: 24px;
  overflow: hidden;
  transition: ${({ $resizing }) => ($resizing ? 'none' : widthTransition)};
`;

const SideScroll = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  padding: ${({ theme }) => theme.spacing(2)};
`;

/**
 * The gap between the two panels (width = the retained margin). When resizable,
 * it also carries the marking-page drag pill and a col-resize cursor.
 */
const ResizeHandle = styled.div<{ $shown: boolean; $resizable: boolean }>`
  flex: 0 0 auto;
  width: ${({ theme, $shown }) => ($shown ? theme.spacing(2) : '0px')};
  align-self: stretch;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  cursor: ${({ $resizable }) => ($resizable ? 'col-resize' : 'default')};
  touch-action: none;
  overflow: hidden;
  transition: ${widthTransition};

  ${({ $resizable, theme }) =>
    $resizable &&
    css`
      &::before {
        content: '';
        width: 4px;
        height: 40px;
        background-color: ${theme.palette.gray.main};
        border-radius: 2px;
      }
    `}
`;

const SideDrawer = styled(Drawer)`
  && {
    z-index: ${({ theme }) => theme.zIndex.drawer + 1};
  }
  .MuiDrawer-paper {
    width: 100%;
    box-sizing: border-box;
    padding: ${({ theme }) => theme.spacing(2)};
  }
`;

export { ContentRow, MainContentPanel, ResizeHandle, SideContentPanel, SideDrawer, SideScroll };
